import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";

import { Box, Button, Divider, Grid } from "@mui/material";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";

import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
// import EngineeringIcon from "@mui/icons-material/Engineering";
import AddIcon from "@mui/icons-material/Add";
// import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";
// import RemoveCircleRoundedIcon from "@mui/icons-material/RemoveCircleRounded";

import {
  getQuotationDetail,
  updateStatusProductQuotationPDF,
} from "src/api/quotation";

import { getLocale } from "react-i18nify";
import { device } from "src/styles/device";
import { checkNotEmpty, convertPrice } from "src/common";
import { useDialog } from "src/context/DialogContext";
import { useAuth } from "src/hooks/useAuth";

import Main from "src/components/ui/Container";
import Forbidden from "src/pages/Forbidden";
import UploadPdf from "src/components/quotation/dropzone/UploadPdf";
import Loading from "src/components/ui/Loading";
import { Black, DarkGrey, White } from "src/styles/color";
import ProductDataGrid from "./CreateQuotation/ProductTable";

const Container = styled.div`
  width: 100%;
  max-width: 1240px;
  position: relative;
  margin: 0 auto;
  padding: 32px 60px;

  & > div > p {
    word-wrap: break-word;
  }

  @media ${device.mobileL} {
    padding: 24px;
  }
`;

const DivBackground = styled.div`
  width: 100%;
  padding: 32px 40px;
  background-color: ${White};
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.1);
  border-radius: 24px;

  @media ${device.tabletL} {
    padding: 32px 40px;
  }
  @media ${device.mobileL} {
    padding: 0;
    border: none;
    box-shadow: none;
  }
`;

const WrapperHeader = styled.div`
  width: max-content;
  display: flex;
  align-items: center;
  gap: 30px;
  margin-bottom: 16px;
`;

const SpanTitle = styled.span`
  color: #000;
  font-size: 1.5rem;
  font-weight: 600;
  text-align: center;
`;

const SpanSubTitle = styled.div`
  color: #bcbcc0;
  font-size: 1rem;
  font-weight: 600;

  span {
    font-size: 1rem;
    font-weight: 400;
  }
`;

const SpanHeader = styled.p`
  color: ${Black};
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  margin-bottom: 8px;
`;

const SpanSubHeader = styled.p`
  color: ${DarkGrey};
  font-size: 0.75rem;
  font-weight: 400;
  margin: 0;
  margin-bottom: 8px;
`;

const SpanContent = styled.p`
  color: #000;
  font-size: 1rem;
  font-weight: 400;
  margin: 0;
  /* margin-bottom: 16px; */
`;

const StatusChip = styled(Chip)`
  & .MuiChip-label {
    font-weight: 500;
  }
`;

const WrapperTableGrid = styled.div`
  width: 100%;
  background-color: ${White};
  border-radius: 24px;
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.1);
  margin-top: 24px;

  @media ${device.tabletL} {
    padding-bottom: 0;
    margin-bottom: 24px;
    /* box-shadow: none; */
    border-radius: 16px;
  }
`;

const WrapperTextField = styled.div`
  width: 100%;

  .TextField-without-border-radius fieldset {
    border-radius: 16px;
    // text-align: center;
    // background: #fff;
  }
  .MuiOutlinedInput-input {
    background: #fff;
    /* text-align: center; */
    border-radius: 16px;
  }
  .MuiInputBase-root-MuiOutlinedInput-root {
    border-radius: 16px;
    background: #fff;
  }
  .MuiInputBase-root-MuiOutlinedInput-root-MuiSelect-root {
    border-radius: 16px;
  }
`;

const RowSumPrice = styled.div`
  margin-left: 0;
  margin-right: 0;
  justify-content: space-between;
  /* margin-top: 16px; */
  margin-bottom: 16px;
  display: flex;
  /* padding: 0 20px; */

  :last-child {
    margin-bottom: 0;
  }
`;

const WrapperCalQuo = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  /* margin: 30px 0 30px 0; */
  padding: 30px 37px;
  border-radius: 16px;
  background-color: #f3f1f2;

  @media ${device.tablet} {
    margin: 20px 0 20px 0;
  }
  @media ${device.mobile} {
    margin: 10px 0 10px 0;
  }
`;

const Price = styled.span`
  color: ${(props) => props.fontColor};
  font-size: 1.125rem;
  font-weight: ${(props) => (props.bold ? 600 : 400)};
  text-align: ${(props) => (props.align ? props.align : "left")};

  @media ${device.tablet} {
    font-size: 0.75rem;
  }
  @media ${device.tablet} {
    font-size: 0.625rem !important;
  }
`;

const QuotationDetail = ({ open }) => {
  const { user, checkAllowPermission } = useAuth();
  let { quotationId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { openDialog, closeDialog } = useDialog();

  const [quotationDetail, setQuotationDetail] = useState({});
  const [isAddQuotPdf, setIsAddQuotPdf] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const fetchQuotationDetail = async () => {
    try {
      setIsLoading(true);

      const { data } = await getQuotationDetail(quotationId);
      setQuotationDetail(data);
    } catch (err) {
      console.error("fetch detail error: ", err.message);
      enqueueSnackbar(err.response.data.message, { variant: "error" });
      navigate("/404");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (quotationId === undefined) return;

    fetchQuotationDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotationId]);

  const discountSelected = () => {
    const priceArray = quotationDetail?.product_choice_quotations.map(
      (product) => {
        if (product?.product_choice_quotation?.group_price?.special_price > 0)
          return (
            product?.product_choice_quotation?.group_price?.special_price *
            product.volume
          );
        return 0;
      },
    );
    if (priceArray?.reduce((a, b) => a + b, 0) > 0)
      return (
        quotationDetail.product_choice_quotations
          ?.map((product) => product.price * product.volume, 0)
          .reduce((a, b) => a + b, 0) - priceArray?.reduce((a, b) => a + b, 0)
      );
    else return 0;
  };

  const priceSelected = () => {
    const priceArray = quotationDetail?.product_choice_quotations?.map(
      (product) => {
        if (product?.product_choice_quotation?.group_price?.special_price > 0)
          return (
            product?.product_choice_quotation?.group_price?.special_price *
            product.volume
          );
        return product.price * product.volume;
      },
    );
    return priceArray?.reduce((a, b) => a + b, 0) || 0;
  };

  const handleApprove = () => {
    if (
      quotationDetail.quotation_price_pdf_first === null ||
      quotationDetail.quotation_price_pdf_first === undefined ||
      quotationDetail.quotation_price_pdf_first === ""
    ) {
      enqueueSnackbar(`กรุณาอัพโหลดเอกสาร Pdf`, { variant: "error" });
      return;
    } else {
      // const params =
      // {
      //   sample: quotationDetail?.product_choice_quotations.map((product) => {
      //     product.sample_id = quotationDetail.id;
      //     return product;
      //   }),
      // };
      // const params = null;
      let data = new FormData();
      const {
        quotation_price_pdf_first,
        quotation_price_pdf_second,
        additional_document_pdf,
        ...quoDetail
      } = quotationDetail;

      const quoDetailParams = {
        ...quoDetail,
        ...(checkNotEmpty(quotation_price_pdf_first)
          ? { quotation_price_pdf_first }
          : {}),
        ...(checkNotEmpty(quotation_price_pdf_second)
          ? { quotation_price_pdf_second }
          : {}),
        ...(checkNotEmpty(additional_document_pdf)
          ? { additional_document_pdf }
          : {}),
      };

      Object.keys(quoDetailParams).forEach((key) => {
        if (Array.isArray(quoDetailParams[key])) {
          data.append(key, JSON.stringify(quoDetailParams[key]));
        } else {
          data.append(key, quoDetailParams[key]);
        }
        return data;
      });

      updateStatusProductQuotationPDF(quotationId, data)
        .then((res) => {
          enqueueSnackbar(`อนุมัติเอกสารสำเร็จ`, { variant: "success" });
          setTimeout(() => {
            (async () => {
              await getQuotationDetail(quotationId)
                .then(async ({ data }) => {
                  setQuotationDetail(data);
                })
                .catch((err) => {
                  console.log("error", err.message);
                });
            })();
          }, 1500);
        })
        .catch((err) => {
          enqueueSnackbar(err.response.data.message, { variant: "error" });
        });
    }
  };

  if (user?.role_id === 3 || user?.role_id === 4)
    return (
      <Main
        sx={{ width: "100%", marginTop: "75px", background: "#fff" }}
        open={open}
      >
        <Forbidden />
      </Main>
    );

  return (
    <>
      <Main
        sx={{ width: "100%", marginTop: "75px", background: "#fff" }}
        open={open}
      >
        <Container>
          <WrapperHeader>
            <SpanTitle>
              {getLocale() === "th"
                ? "รายละเอียดใบขอเสนอราคา"
                : "Quotaion detail"}
            </SpanTitle>

            {quotationDetail?.last_update_quotation ? (
              <SpanSubTitle>
                แก้ไขล่าสุด :{" "}
                <span>
                  {dayjs(
                    quotationDetail?.last_update_quotation?.last_update,
                  ).format("DD/MM/YYYY")}
                </span>{" "}
                โดย :{" "}
                <span>{`${quotationDetail?.last_update_quotation?.admin?.first_name} ${quotationDetail?.last_update_quotation?.admin?.last_name}`}</span>
              </SpanSubTitle>
            ) : null}
          </WrapperHeader>

          <Grid container spacing={4}>
            <Grid item xs={12}>
              <DivBackground>
                <Grid container rowSpacing={2} columnSpacing={3}>
                  <Grid item xs={12}>
                    <SpanSubHeader>
                      {getLocale() === "th" ? "เลขที่เอกสาร " : "Document No. "}
                    </SpanSubHeader>
                    <Box display="flex" alignItems="center" gap={2}>
                      <SpanContent>
                        {quotationDetail?.running_number}
                      </SpanContent>

                      <StatusChip
                        label={
                          quotationDetail?.status === 0
                            ? "Pending"
                            : quotationDetail?.status === 1
                              ? "Approve"
                              : quotationDetail?.status === 2
                                ? "Reject"
                                : "-"
                        }
                        color={
                          quotationDetail?.status === 0
                            ? "warning"
                            : quotationDetail?.status === 1
                              ? "success"
                              : quotationDetail?.status === 2
                                ? "error"
                                : "default"
                        }
                      />
                    </Box>
                  </Grid>

                  {/* <Grid item xs="auto">
                    <SpanContent>
                      {quotationDetail?.status === 0
                        ? "Pending"
                        : quotationDetail?.status === 1
                          ? "Approve"
                          : quotationDetail?.status === 2
                            ? "Reject"
                            : "-"}
                    </SpanContent>
                  </Grid> */}

                  <Grid item xs={12}>
                    <Divider
                      sx={{
                        borderWidth: "6px",
                        borderColor: "var(--darkwhite-color)",
                        margin: { xs: "0 -24px", sm: "0 -40px" },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <SpanHeader>ข้อมูลลูกค้า</SpanHeader>
                  </Grid>

                  <Grid item xs={6}>
                    <SpanSubHeader>
                      {getLocale() === "th"
                        ? "ชื่อลูกค้า / ชื่อบริษัท"
                        : "Customer Name / Company Name"}
                    </SpanSubHeader>
                    <SpanContent>
                      {quotationDetail?.full_name
                        ? quotationDetail?.full_name
                        : "-"}
                    </SpanContent>
                  </Grid>

                  <Grid item xs={6}>
                    <SpanSubHeader>
                      {getLocale() === "th" ? "เบอร์โทรศัพท์" : "Phone Number"}
                    </SpanSubHeader>
                    <SpanContent>
                      {quotationDetail?.phone_number
                        ? quotationDetail?.phone_number
                        : "-"}
                    </SpanContent>
                  </Grid>

                  <Grid item xs={6}>
                    <SpanSubHeader>
                      {getLocale() === "th" ? "สาขา" : "Branch"}
                    </SpanSubHeader>
                    <SpanContent>
                      {quotationDetail?.branch ? quotationDetail?.branch : "-"}
                    </SpanContent>
                  </Grid>

                  <Grid item xs={6}>
                    <SpanSubHeader>
                      {getLocale() === "th" ? "อีเมล" : "E-mail"}
                    </SpanSubHeader>
                    <SpanContent>
                      {quotationDetail?.email ? quotationDetail?.email : "-"}
                    </SpanContent>
                  </Grid>

                  <Grid item xs={6}>
                    <SpanSubHeader>
                      {getLocale() === "th"
                        ? "เลขประจำตัวผู้เสียภาษี"
                        : "Tax Number"}
                    </SpanSubHeader>
                    <SpanContent>
                      {quotationDetail?.tax_id ? quotationDetail?.tax_id : "-"}
                    </SpanContent>
                  </Grid>

                  <Grid item xs={6}>
                    <SpanSubHeader>
                      {getLocale() === "th" ? "โทรสาร" : "Fax"}
                    </SpanSubHeader>
                    <SpanContent>
                      {quotationDetail?.fax ? quotationDetail?.fax : "-"}
                    </SpanContent>
                  </Grid>

                  <Grid item xs={12}>
                    <SpanSubHeader>
                      {getLocale() === "th" ? "ที่อยู่" : "Address"}
                    </SpanSubHeader>
                    <SpanContent>
                      {!!quotationDetail?.address
                        ? !!quotationDetail?.province &&
                          !!quotationDetail?.district &&
                          !!quotationDetail?.sub_district &&
                          !!quotationDetail?.postal_code
                          ? `${quotationDetail?.address} ${quotationDetail?.sub_district} ${quotationDetail?.district} ${quotationDetail?.province} ${quotationDetail?.postal_code}`
                          : `${quotationDetail?.address}`
                        : "-"}
                    </SpanContent>
                  </Grid>

                  {quotationDetail?.order_ref ||
                  (quotationDetail?.ref_doc !== null &&
                    quotationDetail?.ref_doc !== "null" &&
                    quotationDetail?.ref_doc !== "" &&
                    quotationDetail?.ref_doc !== undefined) ? (
                    <>
                      <Grid item xs={6}>
                        <SpanSubHeader>
                          {getLocale() === "th"
                            ? "ออเดอร์อ้างอิง"
                            : "Order ref."}
                        </SpanSubHeader>

                        {quotationDetail?.order_ref ? (
                          <SpanContent
                            style={{
                              cursor: "pointer",
                              textDecoration: "underline",
                              width: "100%",
                            }}
                            onClick={() => {
                              window.open(
                                `/order/detail/${quotationDetail?.order_ref?._id}`,
                                "_blank",
                              );
                            }}
                          >
                            {quotationDetail?.order_ref?.running_number}
                          </SpanContent>
                        ) : (
                          <SpanContent>-</SpanContent>
                        )}
                      </Grid>

                      <Grid item xs={6}>
                        <SpanSubHeader>
                          {getLocale() === "th"
                            ? "อ้างอิงเอกสารเดิม"
                            : "Replacement Document"}
                        </SpanSubHeader>

                        {quotationDetail?.ref_doc?.running_number ? (
                          <SpanContent
                            style={{
                              cursor: "pointer",
                              textDecoration: "underline",
                              width: "100%",
                            }}
                            onClick={() => {
                              window.open(
                                `/quotation/detail/${quotationDetail?.ref_doc?._id}`,
                                "_blank",
                              );
                            }}
                          >
                            {quotationDetail?.ref_doc?.running_number}
                          </SpanContent>
                        ) : (
                          <SpanContent>-</SpanContent>
                        )}
                      </Grid>
                    </>
                  ) : null}
                </Grid>
              </DivBackground>
            </Grid>

            <Grid item xs={12}>
              <SpanTitle>
                {getLocale() === "th"
                  ? "สินค้าในใบเสนอราคานี้"
                  : "สินค้าในใบเสนอราคานี้"}
              </SpanTitle>

              {!!quotationDetail?.product_choice_quotations &&
                quotationDetail?.product_choice_quotations?.length > 0 && (
                  <WrapperTableGrid>
                    <ProductDataGrid
                      formData={quotationDetail}
                      isPreview={true}
                    />
                  </WrapperTableGrid>
                )}
            </Grid>

            {/* discount field (hold) */}
            {/* <Grid item xs={12}>
            <SpanTitle>{getLocale() === "th" ? "ส่วนลด" : "ส่วนลด"}</SpanTitle>

            <WrapperHeader style={{ marginTop: "16px", marginBottom: 0 }}>
              <Price bold>{convertPrice(quotationDetail?.discount?.price ?? 0)}</Price>
              <Price>
                {quotationDetail?.discount?.note || "jkjk"}
              </Price>
            </WrapperHeader>
          </Grid> */}

            <Grid item xs={12}>
              <WrapperCalQuo>
                <RowSumPrice>
                  <Price>{getLocale() === "th" ? "ยอดรวม" : "Subtotal"}</Price>
                  <Price>
                    {convertPrice(
                      quotationDetail?.total_price_of_product ||
                        priceSelected(),
                    )}
                  </Price>
                </RowSumPrice>

                <RowSumPrice>
                  <Price>
                    {getLocale() === "th" ? "รวมราคาช่าง" : "Service total"}
                  </Price>
                  <Price>
                    {convertPrice(quotationDetail?.total_price_of_service || 0)}
                  </Price>
                </RowSumPrice>

                <RowSumPrice className="mx-0 justify-content-between my-2">
                  <Price>{getLocale() === "th" ? "ส่วนลด" : "Discount"}</Price>
                  <Price>
                    {quotationDetail?.status === 0
                      ? convertPrice(discountSelected())
                      : convertPrice(
                          quotationDetail?.product_choice_quotations
                            ?.map((product) => {
                              if (product.last_price)
                                return (
                                  (product.last_price - product.price) *
                                  product.volume
                                );
                              else return 0;
                            })
                            .reduce((a, b) => a + b, 0) || 0,
                        )}
                  </Price>
                </RowSumPrice>

                <RowSumPrice className="mx-0 justify-content-between my-2">
                  <Price>{getLocale() === "th" ? "รวม" : "Total"}</Price>
                  <Price>
                    {quotationDetail?.status === 1
                      ? convertPrice(quotationDetail?.total_price)
                      : convertPrice(priceSelected())}
                  </Price>
                </RowSumPrice>

                <RowSumPrice>
                  <Price>{getLocale() === "th" ? "ภาษี 7%" : "Vat 7%"}</Price>
                  <Price>
                    {quotationDetail?.status === 1
                      ? convertPrice(quotationDetail?.vat)
                      : convertPrice((priceSelected() * 7) / 100)}
                  </Price>
                </RowSumPrice>

                <RowSumPrice>
                  <Price bold>
                    {getLocale() === "th" ? "รวมทั้งหมด" : "Grand total"}
                  </Price>
                  <Price bold fontColor="#e13b30">
                    {quotationDetail?.status === 1
                      ? convertPrice(quotationDetail?.total_vat)
                      : convertPrice(
                          priceSelected() + (priceSelected() * 7) / 100,
                        )}
                  </Price>
                </RowSumPrice>
              </WrapperCalQuo>
            </Grid>

            {quotationDetail?.status === 0 ? ( // status: pending
              checkAllowPermission("quotations.can_approve") && (
                <>
                  {/* attach quotation pdf */}
                  <Grid item xs={12}>
                    <SpanTitle>
                      {getLocale() === "th" ? "แนบใบเสนอราคา" : "แนบใบเสนอราคา"}
                      <span style={{ color: "#E13B30" }}> *</span>
                    </SpanTitle>

                    <WrapperTextField
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      <TextField
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <>
                              {quotationDetail?.quotation_price_pdf_first ===
                                null ||
                              quotationDetail?.quotation_price_pdf_first ===
                                "" ||
                              quotationDetail?.quotation_price_pdf_first ===
                                undefined ? (
                                <UploadPdf
                                  callBackFile={(fileCallBlack) => {
                                    setQuotationDetail({
                                      ...quotationDetail,
                                      quotation_price_pdf_first: fileCallBlack,
                                    });
                                  }}
                                  closeAlert={false}
                                >
                                  <PictureAsPdfIcon
                                    sx={{
                                      color:
                                        quotationDetail?.quotation_price_pdf_first !==
                                          null &&
                                        quotationDetail?.quotation_price_pdf_first !==
                                          "" &&
                                        quotationDetail?.quotation_price_pdf_first !==
                                          undefined
                                          ? "#e04132"
                                          : "rgba(0, 0, 0, 0.54)",
                                      cursor: "pointer",
                                      ":hover": {
                                        color: "#e04132",
                                      },
                                    }}
                                  />
                                </UploadPdf>
                              ) : (
                                <IconButton
                                  sx={{ padding: 0 }}
                                  onClick={() => {
                                    setQuotationDetail({
                                      ...quotationDetail,
                                      quotation_price_pdf_first: "",
                                    });
                                  }}
                                >
                                  <CloseIcon
                                    sx={{
                                      color: "#e04132",
                                      cursor: "pointer",
                                    }}
                                  />
                                </IconButton>
                              )}
                            </>
                          ),
                        }}
                        InputLabelProps={{ shrink: true }}
                        label="PDF (My Account)"
                        variant="outlined"
                        className="TextField-without-border-radius"
                        sx={{
                          my: 2,
                          width: isAddQuotPdf ? "100%" : "95%",
                          textAlign: "left",
                          background: "#fff",
                          borderRadius: "16px",
                        }}
                        value={
                          typeof quotationDetail?.quotation_price_pdf_first !==
                          "object"
                            ? quotationDetail?.quotation_price_pdf_first
                            : quotationDetail?.quotation_price_pdf_first?.name
                        }
                      />

                      {!isAddQuotPdf && (
                        <IconButton
                          sx={{
                            padding: 0,
                            width: "30px",
                            height: "30px",
                            borderRadius: "50%",
                            border: "1px solid #E13B30",
                            marginLeft: "16px",
                          }}
                          onClick={() => setIsAddQuotPdf(true)}
                        >
                          <AddIcon
                            sx={{
                              color: "#e04132",
                              cursor: "pointer",
                              width: "100%",
                            }}
                          />
                        </IconButton>
                      )}
                    </WrapperTextField>

                    {isAddQuotPdf && (
                      <WrapperTextField
                        style={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <TextField
                          InputProps={{
                            readOnly: true,
                            endAdornment: (
                              <>
                                {quotationDetail?.quotation_price_pdf_second ===
                                  null ||
                                quotationDetail?.quotation_price_pdf_second ===
                                  "" ||
                                quotationDetail?.quotation_price_pdf_second ===
                                  undefined ? (
                                  <UploadPdf
                                    callBackFile={(fileCallBlack) => {
                                      setQuotationDetail({
                                        ...quotationDetail,
                                        quotation_price_pdf_second:
                                          fileCallBlack,
                                      });
                                    }}
                                    closeAlert={false}
                                  >
                                    <PictureAsPdfIcon
                                      sx={{
                                        color:
                                          quotationDetail?.quotation_price_pdf_second !==
                                            null &&
                                          quotationDetail?.quotation_price_pdf_second !==
                                            "" &&
                                          quotationDetail?.quotation_price_pdf_second !==
                                            undefined
                                            ? "#e04132"
                                            : "rgba(0, 0, 0, 0.54)",
                                        cursor: "pointer",
                                        ":hover": {
                                          color: "#e04132",
                                        },
                                      }}
                                    />
                                  </UploadPdf>
                                ) : (
                                  <IconButton
                                    sx={{ padding: 0 }}
                                    onClick={() => {
                                      setQuotationDetail({
                                        ...quotationDetail,
                                        quotation_price_pdf_second: "",
                                      });
                                    }}
                                  >
                                    <CloseIcon
                                      sx={{
                                        color: "#e04132",
                                        cursor: "pointer",
                                      }}
                                    />
                                  </IconButton>
                                )}
                              </>
                            ),
                          }}
                          InputLabelProps={{ shrink: true }}
                          label="PDF (My Account)"
                          variant="outlined"
                          className="TextField-without-border-radius"
                          sx={{
                            my: 1,
                            width: "95%",
                            textAlign: "left",
                            background: "#fff",
                            borderRadius: "16px",
                          }}
                          value={
                            typeof quotationDetail?.quotation_price_pdf_second !==
                            "object"
                              ? quotationDetail?.quotation_price_pdf_second
                              : quotationDetail?.quotation_price_pdf_second
                                  ?.name
                          }
                        />

                        <IconButton
                          sx={{
                            padding: "5px",
                            width: "30px",
                            height: "30px",
                            borderRadius: "50%",
                            backgroundColor: "#BCBCC0",
                            marginLeft: "16px",
                          }}
                          onClick={() => setIsAddQuotPdf(false)}
                        >
                          <CloseIcon
                            sx={{
                              color: "#ffffff",
                              cursor: "pointer",
                              width: "100%",
                            }}
                          />
                        </IconButton>
                      </WrapperTextField>
                    )}
                  </Grid>

                  {/* attach addition pdf */}
                  <Grid item xs={12}>
                    <SpanTitle>
                      {getLocale() === "th"
                        ? "แนบเอกสารเพิ่มเติม"
                        : "แนบเอกสารเพิ่มเติม"}
                    </SpanTitle>

                    <WrapperTextField>
                      <TextField
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <>
                              {quotationDetail?.additional_document_pdf ===
                                null ||
                              quotationDetail?.additional_document_pdf === "" ||
                              quotationDetail?.additional_document_pdf ===
                                undefined ? (
                                <UploadPdf
                                  callBackFile={(fileCallBlack) => {
                                    setQuotationDetail({
                                      ...quotationDetail,
                                      additional_document_pdf: fileCallBlack,
                                    });
                                  }}
                                  closeAlert={false}
                                >
                                  <PictureAsPdfIcon
                                    sx={{
                                      color:
                                        quotationDetail?.additional_document_pdf !==
                                          null &&
                                        quotationDetail?.additional_document_pdf !==
                                          "" &&
                                        quotationDetail?.additional_document_pdf !==
                                          undefined
                                          ? "#e04132"
                                          : "rgba(0, 0, 0, 0.54)",
                                      cursor: "pointer",
                                      ":hover": {
                                        color: "#e04132",
                                      },
                                    }}
                                  />
                                </UploadPdf>
                              ) : (
                                <IconButton
                                  sx={{ padding: 0 }}
                                  onClick={() => {
                                    setQuotationDetail({
                                      ...quotationDetail,
                                      additional_document_pdf: "",
                                    });
                                  }}
                                >
                                  <CloseIcon
                                    sx={{
                                      color: "#e04132",
                                      cursor: "pointer",
                                    }}
                                  />
                                </IconButton>
                              )}
                            </>
                          ),
                        }}
                        InputLabelProps={{ shrink: true }}
                        label="PDF"
                        variant="outlined"
                        className="TextField-without-border-radius"
                        sx={{
                          my: 2,
                          width: "100%",
                          textAlign: "left",
                          background: "#fff",
                          borderRadius: "16px",
                        }}
                        value={
                          typeof quotationDetail?.additional_document_pdf !==
                          "object"
                            ? quotationDetail?.additional_document_pdf
                            : quotationDetail?.additional_document_pdf?.name
                        }
                      />
                    </WrapperTextField>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      disabled={
                        quotationDetail?.quotation_price_pdfs?.length === 0 &&
                        !checkNotEmpty(
                          quotationDetail?.quotation_price_pdf_first,
                        )
                      }
                      // onClick={() => setIsVisibleConfirmApprove(true)}
                      onClick={() =>
                        openDialog({
                          type: "confirm",
                          title:
                            getLocale() === "th"
                              ? `ต้องการอนุมัติคำขอหรือไม่ ?`
                              : `Want to approve document?`,
                          onConfirm: () => {
                            handleApprove();
                            closeDialog();
                          },
                          maxWidth: 342,
                        })
                      }
                      className="Button-New-Brand"
                      sx={{
                        background: "#e04132",
                        color: "#fff",
                        borderRadius: "16px",
                        margin: "32px auto",
                        display: "flex",
                        width: "120px",
                        height: "42px",
                        "&.Mui-disabled": {
                          background: "grey",
                          color: "#fff",
                        },
                        "&:hover": {
                          background: "#e0413280",
                          color: "grey",
                        },
                      }}
                    >
                      อนุมัติ
                    </Button>
                  </Grid>
                </>
              )
            ) : (
              <>
                {/* show quotation pdf */}
                {checkNotEmpty(quotationDetail?.quotation_price_pdfs) ? (
                  <Grid item xs={12}>
                    <WrapperHeader>
                      <SpanTitle>
                        {getLocale() === "th"
                          ? "แนบใบเสนอราคา"
                          : "แนบใบเสนอราคา"}
                      </SpanTitle>

                      <Chip
                        label="PDF (My Account)"
                        sx={{
                          ".MuiChip-label": {
                            fontSize: "0.75rem",
                            color: "#6C6C70",
                            padding: "0 16px",
                          },
                        }}
                      />
                    </WrapperHeader>

                    {quotationDetail?.quotation_price_pdfs ? (
                      quotationDetail?.quotation_price_pdfs.map(
                        (pdf, index) => (
                          <Chip
                            key={`quotation-pdf-${index}`}
                            label={pdf?.quotation_price_pdf}
                            sx={{
                              height: "auto",
                              marginLeft: "40px",
                              marginBottom: "16px",
                              ".MuiChip-label": {
                                fontSize: "1rem",
                                color: "#6C6C70",
                                padding: "10px 20px",
                                textDecoration: "underline",
                              },
                              ":last-child": {
                                marginBottom: 0,
                              },
                            }}
                            onClick={() => {
                              window.open(pdf?.quotation_price_pdf, "_blank");
                            }}
                          />
                        ),
                      )
                    ) : (
                      <Chip
                        label={quotationDetail?.pdf_url}
                        sx={{
                          height: "auto",
                          marginLeft: "40px",
                          ".MuiChip-label": {
                            fontSize: "1rem",
                            color: "#6C6C70",
                            padding: "10px 20px",
                            textDecoration: "underline",
                          },
                        }}
                        onClick={() => {
                          window.open(quotationDetail?.pdf_url, "_blank");
                        }}
                      />
                    )}
                  </Grid>
                ) : null}

                {/* show addition pdf */}
                {checkNotEmpty(quotationDetail?.additional_document_pdf) ? (
                  <Grid item xs={12}>
                    <WrapperHeader>
                      <SpanTitle>
                        {getLocale() === "th"
                          ? "แนบเอกสารเพิ่มเติม"
                          : "แนบเอกสารเพิ่มเติม"}
                        <span style={{ color: "#E13B30" }}> *</span>
                      </SpanTitle>

                      <Chip
                        label="PDF"
                        sx={{
                          ".MuiChip-label": {
                            fontSize: "0.75rem",
                            color: "#6C6C70",
                            padding: "0 16px",
                          },
                        }}
                      />
                    </WrapperHeader>

                    <Chip
                      label={quotationDetail?.additional_document_pdf}
                      sx={{
                        height: "auto",
                        marginLeft: "40px",
                        ".MuiChip-label": {
                          fontSize: "1rem",
                          color: "#6C6C70",
                          padding: "10px 20px",
                          textDecoration: "underline",
                        },
                      }}
                      onClick={() => {
                        window.open(
                          quotationDetail?.additional_document_pdf,
                          "_blank",
                        );
                      }}
                    />
                  </Grid>
                ) : null}
              </>
            )}
          </Grid>
        </Container>
      </Main>

      {isLoading && <Loading />}
    </>
  );
};
export default QuotationDetail;
